import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { RolesService } from '../services/roles.service';
import { PermissionsService } from '../services/permissions.service';

export default function RoleForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    status: 'active',
    permissions: []
  });

  const [permissionsByModule, setPermissionsByModule] = useState({});

  useEffect(() => {
    const initialize = async () => {
      try {
        // Fetch all permissions
        const permRes = await PermissionsService.getAllPermissions();
        const perms = Array.isArray(permRes) ? permRes : (permRes?.data || []);
        
        // Group permissions by module
        const grouped = perms.reduce((acc, perm) => {
          const mod = perm.module || 'Other';
          if (!acc[mod]) acc[mod] = [];
          acc[mod].push(perm);
          return acc;
        }, {});
        setPermissionsByModule(grouped);

        // Fetch existing role if edit mode
        if (isEditMode) {
          const roleRes = await RolesService.getRole(id);
          const roleData = roleRes?.data || roleRes;
          if (roleData) {
            setFormData({
              name: roleData.name || '',
              display_name: roleData.display_name || '',
              description: roleData.description || '',
              status: roleData.status || 'active',
              // Extract permission IDs if they are objects, else just use the array
              permissions: Array.isArray(roleData.permissions) 
                ? roleData.permissions.map(p => p._id || p.id || p)
                : []
            });
          }
        }
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setIsFetching(false);
      }
    };

    initialize();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissionToggle = (permId) => {
    setFormData(prev => {
      const perms = [...prev.permissions];
      if (perms.includes(permId)) {
        return { ...prev, permissions: perms.filter(id => id !== permId) };
      } else {
        return { ...prev, permissions: [...perms, permId] };
      }
    });
  };

  const handleModuleSelectAll = (modulePerms, isSelected) => {
    const modulePermIds = modulePerms.map(p => p._id || p.id);
    setFormData(prev => {
      let perms = [...prev.permissions];
      if (isSelected) {
        // Remove all
        perms = perms.filter(id => !modulePermIds.includes(id));
      } else {
        // Add all
        modulePermIds.forEach(id => {
          if (!perms.includes(id)) perms.push(id);
        });
      }
      return { ...prev, permissions: perms };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.display_name) {
      toast.error('Name and Display Name are required');
      return;
    }

    setIsLoading(true);
    try {
      if (isEditMode) {
        await RolesService.updateRole(id, formData);
        toast.success('Role updated successfully');
      } else {
        await RolesService.createRole(formData);
        toast.success('Role created successfully');
      }
      navigate('/roles');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save role');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return <div className="page-content"><p>Loading...</p></div>;
  }

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button 
          onClick={() => navigate('/roles')}
          style={{ 
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '40px', height: '40px', borderRadius: '50%',
            border: '1px solid var(--border-color)', background: 'white',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={20} color="var(--text-secondary)" />
        </button>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
            {isEditMode ? 'Edit Role' : 'Create New Role'}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Configure role details and assign module-level permissions
          </p>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
        <form onSubmit={handleSubmit}>
          
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: 'var(--primary-orange)' }}>Role Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
            <div className="form-group">
              <label>Role Name *</label>
              <input 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
                placeholder="e.g. admin, editor" 
                required 
              />
            </div>
            <div className="form-group">
              <label>Display Name *</label>
              <input 
                name="display_name" 
                value={formData.display_name} 
                onChange={handleChange} 
                placeholder="e.g. Administrator" 
                required 
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Description</label>
              <textarea 
                name="description" 
                value={formData.description} 
                onChange={handleChange} 
                placeholder="Describe what this role can do..." 
                rows={3}
              />
            </div>
          </div>

          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: 'var(--primary-orange)', borderTop: '1px solid var(--border-color)', paddingTop: '24px' }}>
            Module Permissions
          </h3>
          
          {Object.keys(permissionsByModule).length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>No permissions found. Create permissions first.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
              {Object.entries(permissionsByModule).map(([moduleName, perms]) => {
                const modulePermIds = perms.map(p => p._id || p.id);
                const isAllSelected = modulePermIds.length > 0 && modulePermIds.every(id => formData.permissions.includes(id));
                const isIndeterminate = modulePermIds.some(id => formData.permissions.includes(id)) && !isAllSelected;

                return (
                  <div key={moduleName} style={{ border: '1px solid var(--border-color)', borderRadius: '12px', padding: '16px', background: '#FAFAFA' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', textTransform: 'capitalize', fontSize: '14px' }}>
                        {moduleName}
                      </span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', color: 'var(--primary-orange)' }}>
                        <input 
                          type="checkbox" 
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isIndeterminate; }}
                          onChange={() => handleModuleSelectAll(perms, isAllSelected)}
                          style={{ cursor: 'pointer' }}
                        />
                        Select All
                      </label>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {perms.map(perm => {
                        const permId = perm._id || perm.id;
                        return (
                          <label key={permId} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                            <input 
                              type="checkbox" 
                              checked={formData.permissions.includes(permId)}
                              onChange={() => handlePermissionToggle(permId)}
                              style={{ cursor: 'pointer' }}
                            />
                            {perm.display_name || perm.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
            <button 
              type="button" 
              onClick={() => navigate('/roles')}
              style={{ padding: '12px 24px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'white', cursor: 'pointer', fontWeight: '500' }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isLoading}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '10px', border: 'none', background: 'var(--primary-orange)', color: 'white', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: '600' }}
            >
              <Save size={18} />
              {isLoading ? 'Saving...' : 'Save Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
